import { reactive, ref, onMounted, onUnmounted } from 'vue';
import { appwriteConfig, client, functions, databases, query } from './appwrite';
import { useSettings } from './settings';

const DATABASE_ID = appwriteConfig.database;
const COLLECTION_ID = appwriteConfig.collections.checkrides;

let instance = null;
let unsubscribe = null;

export function useCheckrides() {
    if (instance) return instance;

    const checkrides = reactive([]);
    const isInitialized = ref(false);

    const parseCheckride = (doc) => {
        return {
            id: doc.$id,
            scheduledDatetime: doc.scheduledDatetime,
            routing: doc.routing,
            picName: doc.picName,
            picId: doc.picId,
            sicName: doc.sicName,
            sicId: doc.sicId,
            checkAirmanName: doc.checkAirmanName,
            checkAirmanId: doc.checkAirmanId,
            tailNumber: doc.tailNumber,
            type: doc.type,
            picCheckResult: doc.picCheckResult,
            picCheckMessage: doc.picCheckMessage,
            sicCheckResult: doc.sicCheckResult,
            sicCheckMessage: doc.sicCheckMessage,
            checkAirmanCheckResult: doc.checkAirmanCheckResult,
            checkAirmanCheckMessage: doc.checkAirmanCheckMessage,
            deletedAt: doc.deletedAt,
            createdAt: doc.$createdAt,
            updatedAt: doc.$updatedAt
        };
    };

    const fetchTailNumbers = async () => {
        // Fetch all tail numbers using function call
        const response = await functions.createExecution(appwriteConfig.functions.fetchTailNumbers);
        const result = JSON.parse(response.responseBody);

        if (result.success) {
            if (result.tailNumbers) {
                const filtered = {};
                for (const key in result.tailNumbers) {
                    if (result.tailNumbers[key] !== "Unassigned") {
                        filtered[key] = result.tailNumbers[key];
                    }
                }
                return filtered;
            }
            return result.tailNumbers || [];
        } else {
            console.error('Error fetching tail numbers:', result.error);
            return [];
        }
    };

    const fetchCheckAirmen = async () => {
        const response = await functions.createExecution(appwriteConfig.functions.fetchCheckAirmen);
        const result = JSON.parse(response.responseBody);

        if (result.success) {
            return result.checkAirmen || [];
        } else {
            console.error('Error fetching check airmen:', result.error);
            return [];
        }
    };

    const fetchPilots = async () => {
        const response = await functions.createExecution(appwriteConfig.functions.fetchPilots);
        const result = JSON.parse(response.responseBody);

        if (result.success) {
            return result.pilots || [];
        } else {
            console.error('Error fetching pilots:', result.error);
            return [];
        }
    };

    const fetchLegs = async (aircraftId) => {
        const response = await functions.createExecution(
            appwriteConfig.functions.fetchLegs,
            JSON.stringify({ aircraftOid: aircraftId })
        );
        const result = JSON.parse(response.responseBody);

        if (result.success) {
            let legsArray = [];
            if (result.legs && typeof result.legs === 'object') {
                legsArray = Object.entries(result.legs).map(([key, value]) => ({
                    id: key,
                    ...value
                }));
            }
            return legsArray.filter(leg => leg !== "Unassigned");
        } else {
            console.error('Error fetching legs:', result.error);
            return [];
        }
    };

    const createCheckride = async (checkrideData) => {
        try {
            const response = await databases.createDocument(
                DATABASE_ID,
                COLLECTION_ID,
                'unique()', // Let Appwrite generate the ID
                {
                    scheduledDatetime: checkrideData.scheduledDatetime,
                    routing: checkrideData.routing,
                    picName: checkrideData.picName,
                    picId: checkrideData.picId,
                    sicName: checkrideData.sicName,
                    sicId: checkrideData.sicId,
                    checkAirmanName: checkrideData.checkAirmanName,
                    checkAirmanId: checkrideData.checkAirmanId,
                    type: checkrideData.type,
                    picCheckResult: checkrideData.picCheckResult || false,
                    picCheckMessage: checkrideData.picCheckMessage || '',
                    sicCheckResult: checkrideData.sicCheckResult || false,
                    sicCheckMessage: checkrideData.sicCheckMessage || '',
                    checkAirmanCheckResult: checkrideData.checkAirmanCheckResult || false,
                    checkAirmanCheckMessage: checkrideData.checkAirmanCheckMessage || ''
                }
            );

            const newCheckride = parseCheckride(response);
            checkrides.push(newCheckride);

            return {
                success: true,
                checkride: newCheckride
            };
        } catch (error) {
            console.error('Error creating checkride:', error);
            return {
                success: false,
                error: error.message || 'Failed to create checkride'
            };
        }
    };

    // Soft delete: stamps deletedAt so the record stays in the collection but
    // disappears from fetches and the local list.
    const deleteCheckride = async (checkrideId) => {
        try {
            await databases.updateDocument(
                DATABASE_ID,
                COLLECTION_ID,
                checkrideId,
                { deletedAt: new Date().toISOString() }
            );

            const index = checkrides.findIndex(c => c.id === checkrideId);
            if (index !== -1) checkrides.splice(index, 1);

            return { success: true };
        } catch (error) {
            console.error('Error deleting checkride:', error);
            return {
                success: false,
                error: error.message || 'Failed to delete checkride'
            };
        }
    };

    const fetchCheckrides = async () => {
        if (!isInitialized.value) {
            initialize();
        }
        try {
            const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
                query.isNull('deletedAt'),
                query.limit(20),
                query.orderDesc('$createdAt')
            ]);
            checkrides.splice(0, checkrides.length, ...response.documents.map(parseCheckride));
            return checkrides;
        } catch (err) {
            console.error('Error fetching checkrides:', err);
            return [];
        }
    };

    // Sends the checkride legality certificate email
    const emailCheckride = async (checkrideId) => {
        try {
            // Get email recipients from settings
            const { getSetting, fetchSettings } = useSettings();

            // Ensure settings are loaded
            if (!getSetting('checkrideEmails')) {
                await fetchSettings?.();
            }

            const emails = getSetting('checkrideEmails');

            const payload = {
                checkrideId,
                emails
            };

            const response = await functions.createExecution(
                appwriteConfig.functions.emailCheckride,
                JSON.stringify(payload)
            );
            const result = JSON.parse(response.responseBody);
            console.log('Email checkride result:', result);
            return result;
        } catch (err) {
            console.error('Error emailing checkride:', err);
            return { success: false, message: 'Failed to email checkride.', error: err.toString() };
        }
    };

    const initialize = () => {
        if (isInitialized.value) return;

        let reconnectTimeout = null;

        const createSubscription = () => {
            if (unsubscribe) {
                unsubscribe();
            }

            return client.subscribe(`databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents`,
                (response) => {
                    response.events.forEach(event => {
                        const doc = response.payload;
                        if (event.includes('create')) {
                            if (!checkrides.find(c => c.id === doc.$id)) {
                                checkrides.push(parseCheckride(doc));
                            }
                        } else if (event.includes('update')) {
                            if (doc.deletedAt) {
                                // Soft-deleted elsewhere — drop it from the list
                                const index = checkrides.findIndex(c => c.id === doc.$id);
                                if (index !== -1) checkrides.splice(index, 1);
                                return;
                            }
                            const existingCheckride = checkrides.find(c => c.id === doc.$id);
                            if (existingCheckride) {
                                Object.assign(existingCheckride, parseCheckride(doc));
                            }
                        } else if (event.includes('delete')) {
                            const index = checkrides.findIndex(c => c.id === doc.$id);
                            if (index !== -1) checkrides.splice(index, 1);
                        }
                    });
                },
                (error) => {
                    console.error('Subscription error:', error);
                    if (reconnectTimeout) {
                        clearTimeout(reconnectTimeout);
                    }
                    reconnectTimeout = setTimeout(() => {
                        console.log('Attempting to reconnect...');
                        unsubscribe = createSubscription();
                    }, 5000);
                }
            );
        };

        unsubscribe = createSubscription();

        const cleanup = () => {
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
            }
            if (unsubscribe) {
                unsubscribe();
            }
        };

        onMounted(fetchCheckrides);
        onUnmounted(cleanup);

        isInitialized.value = true;
    };

    instance = {
        checkrides,
        fetchCheckrides,
        fetchTailNumbers,
        fetchLegs,
        fetchCheckAirmen,
        fetchPilots,
        createCheckride,
        deleteCheckride,
        emailCheckride,
        initialize
    };

    return instance;
}